/**
 * Normalize vehicle-owners API response to an array of owner records.
 * Service getAll returns { success, data, pagination }; some callers pass axios wrappers.
 */
export function extractVehicleOwnersList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (response.data && Array.isArray(response.data.data)) return response.data.data;
  return [];
}

/**
 * Map vehicle IDs to owner names from vehicle_owners.vehicles JSON lists.
 */
export function buildVehicleToOwnerMap(vehicleOwners = []) {
  const map = new Map();
  for (const owner of vehicleOwners) {
    const name = owner.owner_name;
    if (!name) continue;
    for (const vid of owner.vehicles || []) {
      map.set(String(vid), name);
      if (typeof vid === 'number' || /^\d+$/.test(String(vid))) {
        map.set(Number(vid), name);
      }
    }
  }
  return map;
}

export function buildVehicleToOwnerDetailMap(vehicleOwners = []) {
  const map = new Map();
  for (const owner of vehicleOwners || []) {
    for (const vid of owner.vehicles || []) {
      map.set(String(vid), owner);
      if (typeof vid === 'number' || /^\d+$/.test(String(vid))) {
        map.set(Number(vid), owner);
      }
    }
  }
  return map;
}

export function enrichVehiclesWithOwnerDetails(vehicles, vehicleOwners) {
  const map = buildVehicleToOwnerDetailMap(vehicleOwners);
  return (vehicles || []).map((v) => {
    const id = v.id ?? v.vehicle_id;
    const owner = map.get(id) || map.get(String(id));
    if (!owner) {
      return { ...v, owner_name: v.owner_name || null };
    }
    return {
      ...v,
      owner_name: owner.owner_name,
      owner_contact: owner.contact,
      owner_email: owner.email,
      owner_gst: owner.gst,
      owner_percentage: owner.owner_percentage,
      vehicle_owner_id: owner.vehicle_owner_id,
    };
  });
}

export function enrichVehiclesWithOwners(vehicles, vehicleOwners) {
  return enrichVehiclesWithOwnerDetails(vehicles, vehicleOwners).map((v) => ({
    ...v,
    owner_name: v.owner_name || null,
  }));
}

export function formatVehicleLabel(vehicle) {
  if (!vehicle) return '';
  const number = vehicle.vehicle_number || '';
  const type = vehicle.vehicle_type || '';
  const base = type ? `${number} - ${type}` : number;
  if (vehicle.owner_name) {
    return `${base} · Owner: ${vehicle.owner_name}`;
  }
  return base;
}

export function getVehicleOwnerName(vehicleId, vehicleOwners) {
  if (!vehicleId) return null;
  const map = buildVehicleToOwnerMap(vehicleOwners);
  return map.get(vehicleId) || map.get(String(vehicleId)) || null;
}
